import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Loader2, Upload, X, Bug } from 'lucide-react';
import { api } from '../lib/api';
import { deviceInfo, readClientLog } from '../lib/clientLog';

export function BugReportDialog({ open, onOpenChange }) {
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setDescription(''); setScreenshot(null); };

  const readImage = (file) => {
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.readAsDataURL(file);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) readImage(file);
  };

  const handlePaste = (e) => {
    for (const item of e.clipboardData?.items || []) {
      if (item.type?.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          readImage(file);
          toast.success('Screenshot aus der Zwischenablage übernommen');
          e.preventDefault();
          return;
        }
      }
    }
  };

  const submit = async () => {
    if (!description.trim()) { toast.error('Please describe the bug'); return; }
    setSubmitting(true);
    try {
      await api.submitBugReport({
        description: description.trim(), screenshot, page_url: window.location.href,
        device: deviceInfo(), client_log: readClientLog().slice(-25),
      });
      toast.success('Bug report submitted — thank you!');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="bug-report-dialog" onPaste={handlePaste}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Bug className="w-4 h-4 text-red-400" /> Report a Bug
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">What went wrong?</label>
            <Textarea data-testid="bug-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue, what you expected, and steps to reproduce..."
              className="bg-secondary border-border min-h-[120px] text-sm" />
            <p className={`text-[10px] mt-1 ${description.trimStart().startsWith('!') ? 'text-red-400' : 'text-muted-foreground'}`} data-testid="bug-urgent-hint">
              {description.trimStart().startsWith('!')
                ? 'Dringend — wird in der nächsten Nacht gebündelt und an die KI übergeben.'
                : 'Tipp: Mit „!“ am Anfang markierst du den Report als dringend (Nacht-Lauf startet dann sofort, sonst ab 3 offenen Reports).'}
            </p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Screenshot (optional)</label>
            {screenshot ? (
              <div className="relative inline-block">
                <img src={screenshot} alt="preview" className="max-h-40 rounded-sm border border-border" />
                <button onClick={() => setScreenshot(null)} data-testid="remove-screenshot-btn"
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label data-testid="bug-screenshot-label"
                className="flex items-center gap-2 px-3 py-2.5 rounded-sm border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-blue-500 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" /> Bild hochladen — oder Screenshot einfach einfügen (Strg+V)
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="bug-screenshot-input" />
              </label>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="submit-bug-report-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending...</> : 'Submit Report'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
